import {makeAutoObservable, observable, runInAction} from 'mobx';
import type {StudioVersion} from '../../../../../data/api/StudioApi';
import StudioRepository from '../../../../../data/repositories/StudioRepository';
import type {StudioResponse} from '../../../../../data/repositories/StudioRepository';
import {
  ApiError,
  errorMessage,
  GENERIC_ERROR,
  isCreditCheckPending,
  isInsufficientCredits,
} from '../../../errors';
import {
  type EndingDraft,
  endingRequestBody,
} from '../../../components/endingDraft';
import type {
  ReelActions,
  SceneActions,
} from '../../../components/storyboardActions';
import {
  cannotCheckCredits,
  creditsNotSettled,
  hasCreditHold,
} from '../../../creditCheck';
import type StudioJobStore from '../../../store/StudioJobStore';
import type BrandStore from '../../../store/BrandStore';
import {MutationScope, scopedIdentity} from '../../../store/MutationScope';
import {studioRequest} from '../../../store/studioRequests';
import {isClippingJob} from '../../../videoSource';
import type {JobView} from '../../../types';
import {isRenderCurrent} from '../storyboardModel';
import {storyboardText} from '../storyboardText';

export interface Notice {
  kind: 'ok' | 'error';
  text: string;
  /** The request was refused for too few AI credits: the notice links to adding them. */
  addCredits?: boolean;
  /** A credit check left the credits unsettled: the notice goes once they settle. */
  creditCheck?: boolean;
}

/** Where to go after generating: the first microreel of a new render, or the one open. */
export interface RenderResult {
  renders: boolean;
}

const DRAFTING = new Set(['ingesting', 'planning', 'checking']);

/**
 * The storyboard page's state and changes, on the open job of the registered StudioJobStore.
 * The scene editors' own drafts stay in them; this holds what the page shares: its notice,
 * which editors have unsaved edits, the endings to save before generating, and the open
 * theme and branding panels. A page opens it on its job with `open(organizationId, id)`.
 *
 * Changes the page makes (renaming, trying the draft again) show the job they return. A
 * scene's or ending's change resolves to the job, which its editor passes to `saved` while it
 * still shows what it changed. Registered in storesProvider as `studioStoryboardStore`.
 */
export default class StoryboardStore {
  rendering = false;
  retrying = false;
  canceling = false;
  /** Studio is settling the AI credits a stopped generation holds (see `checkCredits`). */
  checkingCredits = false;
  /** Which batch-wide panels (theme, branding) are open; polling never closes them. */
  openSettings: Record<string, boolean> = {};
  /** Editors with unsaved edits, by `${jobId}/scene/${sceneId}`. */
  dirtyEditors: Record<string, boolean> = {};
  /** How many microreels have unsaved ending changes; generating saves them first. */
  pendingEndings = 0;

  private endingDrafts: Record<string, EndingDraft> = {};
  private repository = new StudioRepository();
  private scope = new MutationScope();
  private openedAs: string | null = null;
  /** The message of the last request refused for too few AI credits; see `fail`. */
  private creditsRefusal: string | null = null;
  /** The message of the last request refused for a pending credit check; see `fail`. */
  private creditCheckRefusal: string | null = null;
  /** What the page says above the storyboard; see `notice`. */
  private said: Notice | null = null;
  /** The job's version when the page said it; see `notice`. */
  private saidAt: number | undefined = undefined;

  constructor(
    private jobs: StudioJobStore,
    private brands: BrandStore,
  ) {
    makeAutoObservable<
      StoryboardStore,
      | 'endingDrafts'
      | 'repository'
      | 'scope'
      | 'openedAs'
      | 'creditsRefusal'
      | 'creditCheckRefusal'
      | 'said'
      | 'jobs'
      | 'brands'
    >(
      this,
      {
        said: observable.ref,
        openSettings: observable.ref,
        dirtyEditors: observable.ref,
        endingDrafts: false,
        repository: false,
        scope: false,
        openedAs: false,
        creditsRefusal: false,
        creditCheckRefusal: false,
        jobs: false,
        brands: false,
      },
      {autoBind: true},
    );
  }

  /** Starts afresh on a job; returns what closes it, for an effect's cleanup. */
  open(organizationId: string | undefined, id: string) {
    const identity = scopedIdentity(organizationId, id);
    this.reset();
    this.openedAs = identity;
    this.scope.setIdentity(identity);
    this.scope.activate();
    return () => this.close(identity);
  }

  /** Answers still on their way are ignored; `identity` closes only that opening. */
  close(identity = this.openedAs) {
    if (identity !== this.openedAs) return;
    this.scope.deactivate();
    this.openedAs = null;
  }

  /** The open job, as the job store shows it. */
  get view(): JobView | null {
    return this.jobs.view;
  }

  /**
   * What the page says above the storyboard. That a credit check left the credits unsettled
   * holds only while they still wait for it: once a read of the job shows them settled, or the
   * job has changed since (e.g. a rewrite whose own credits now wait), it goes. Until then it
   * says why they wait as the latest read does, e.g. once a top-up covers them.
   */
  get notice(): Notice | null {
    const notice = this.said;
    if (notice?.addCredits && this.view?.billing?.enforceAiCredits === false)
      return {kind: notice.kind, text: GENERIC_ERROR};
    if (!notice?.creditCheck) return notice;
    return !hasCreditHold(this.view) ||
      this.view?.job.storeVersion !== this.saidAt
      ? null
      : {...notice, text: creditsNotSettled(this.view)};
  }

  set notice(notice: Notice | null) {
    this.said = notice;
    this.saidAt = this.view?.job.storeVersion;
  }

  get drafting() {
    return DRAFTING.has(this.view?.job.status || '');
  }

  get locked() {
    return this.view?.job.status === 'rendering';
  }

  get savingBrand() {
    return this.brands.isSavingFor(this.jobs.organizationId, this.jobs.jobId);
  }

  /**
   * The server refuses every change while the storyboard drafts (it is saved before its
   * source check runs), generates, or other Studio work is queued or running.
   */
  get busy() {
    return this.locked || this.drafting || !!this.view?.running;
  }

  get dirty() {
    return Object.values(this.dirtyEditors).some(Boolean);
  }

  /** The microreels whose scenes have unsaved edits. */
  get dirtyReelIds(): Set<string> {
    const reelOf: Record<string, string> = {};
    for (const reel of this.view?.job.storyboard?.reels || [])
      for (const scene of reel.scenes) reelOf[scene.id] = reel.id;
    return new Set(
      Object.entries(this.dirtyEditors)
        .filter(([, dirty]) => dirty)
        .map(([key]) => reelOf[key.split('/scene/')[1]])
        .filter(Boolean),
    );
  }

  setDirty(key: string, dirty: boolean) {
    if (key in this.dirtyEditors && this.dirtyEditors[key] === dirty) return;
    this.dirtyEditors = {...this.dirtyEditors, [key]: dirty};
  }

  /** An unsaved ending, or null once it is saved or reverted. */
  setEndingDraft(reelId: string, draft: EndingDraft | null) {
    if (draft) this.endingDrafts[reelId] = draft;
    else delete this.endingDrafts[reelId];
    this.pendingEndings = Object.keys(this.endingDrafts).length;
  }

  setSettingOpen(key: string, open: boolean) {
    if (!!this.openSettings[key] === open) return;
    this.openSettings = {...this.openSettings, [key]: open};
  }

  /** Shows the job a change returned and says it was saved. */
  saved(view: JobView) {
    if (!this.jobs.setView(view)) return;
    const count = view.job.storyboard?.reels.length ?? 0;
    this.notice = {
      kind: 'ok',
      text: isRenderCurrent(view)
        ? storyboardText.savedUpToDate(count)
        : storyboardText.savedRegenerate(count, isClippingJob(view.job)),
    };
  }

  /** Shows the job a change returned, without a notice (e.g. a drafted question). */
  showView(view: JobView) {
    return this.jobs.setView(view);
  }

  /**
   * Shows why a change failed. The editors pass their requests' messages; the refusal of one
   * of them (see `send`) for too few AI credits links to adding them. A refusal for a pending
   * credit check reads the job again instead: its own alert says why and what unlocks it.
   */
  fail(text: string) {
    const enforceAiCredits = this.view?.billing?.enforceAiCredits !== false;
    const addCredits = text === this.creditsRefusal;
    const creditCheck = text === this.creditCheckRefusal;
    this.creditsRefusal = null;
    this.creditCheckRefusal = null;
    if (creditCheck) {
      this.notice = null;
      void this.jobs.reload();
      return;
    }
    if (addCredits && !enforceAiCredits) text = GENERIC_ERROR;
    this.notice =
      addCredits && enforceAiCredits
        ? {kind: 'error', text, addCredits}
        : {kind: 'error', text};
  }

  /** Shows why the page's own request failed, as `fail` does. */
  private failWith(error: unknown) {
    this.refused(error);
    this.fail(errorMessage(error));
  }

  /** Remembers why a request was refused, for `fail` to say more than its message. */
  private refused(error: unknown) {
    const text = errorMessage(error);
    this.creditsRefusal = isInsufficientCredits(error) ? text : null;
    this.creditCheckRefusal = isCreditCheckPending(error) ? text : null;
  }

  /** The chosen topics start drafting: the job shows its new state. */
  topicsChosen(view: JobView) {
    this.notice = null;
    this.jobs.setView(view);
  }

  reload() {
    return this.jobs.reload();
  }

  renameJob(title: string) {
    return this.change((organizationId, id, version) =>
      this.repository.renameJob(organizationId, id, title, version),
    );
  }

  async renameReel(reelId: string, title: string) {
    try {
      await this.change((organizationId, id, version) =>
        this.repository.renameReel(organizationId, id, reelId, title, version),
      );
    } catch (e) {
      // A server without the endpoint answers with a bare 404 or 405.
      if (
        e instanceof ApiError &&
        (e.status === 405 || (e.status === 404 && !e.fromServer))
      )
        throw new Error(storyboardText.renameUnsupported);
      throw e;
    }
  }

  /** Drafts the storyboard again after a failed draft, from the saved source and settings. */
  async replan() {
    const view = this.view;
    if (
      !view ||
      this.retrying ||
      this.checkingCredits ||
      view.running ||
      hasCreditHold(view)
    )
      return;
    const current = this.scope.begin();
    this.retrying = true;
    this.notice = null;
    try {
      await this.change((organizationId, id, version) =>
        this.repository.replan(organizationId, id, version),
      );
    } catch (e) {
      if (current()) this.failWith(e);
    } finally {
      if (current())
        runInAction(() => {
          this.retrying = false;
        });
    }
  }

  /**
   * Saves the unsaved endings, then generates the microreels. The ending is not part of the
   * video: when it was the only change, the server keeps the current render and nothing is
   * generated. Resolves to whether it generated, or null when it could not or the page moved on.
   */
  async render(): Promise<RenderResult | null> {
    const view = this.view;
    const storyboard = view?.job.storyboard;
    if (
      !view ||
      !storyboard ||
      this.dirty ||
      this.rendering ||
      this.savingBrand ||
      this.checkingCredits ||
      !view.canRender ||
      this.busy ||
      hasCreditHold(view)
    )
      return null;
    const {organizationId, jobId} = this.jobs;
    const current = this.scope.begin();
    this.rendering = true;
    try {
      let {storeVersion} = view.job;
      let {versionHash} = storyboard;
      let saved: JobView | null = null;
      for (const reel of storyboard.reels) {
        const draft = this.endingDrafts[reel.id];
        if (!draft) continue;
        const body = endingRequestBody(view.job.brief.ending, draft, reel);
        const version = storeVersion;
        saved = await studioRequest(organizationId, org =>
          this.repository.setEnding(org, jobId!, reel.id, body, version),
        );
        if (!current()) return null;
        this.jobs.setView(saved);
        ({storeVersion} = saved.job);
        versionHash = saved.job.storyboard?.versionHash;
      }
      const renders = !saved || !isRenderCurrent(saved);
      if (renders) {
        const version = storeVersion;
        const hash = versionHash;
        await studioRequest(organizationId, org =>
          this.repository.render(org, jobId!, version, hash),
        );
      }
      return current() ? {renders} : null;
    } catch (e) {
      if (current())
        runInAction(() => {
          this.failWith(e);
          this.rendering = false;
        });
      return null;
    }
  }

  /**
   * Asks Studio to settle the AI credits a stopped generation holds, e.g. once the workspace
   * has added some, then reads the job again: settled credits unlock its AI actions. When
   * they still cannot settle (or the server cannot check), the lock stays and the notice
   * says so.
   */
  async checkCredits() {
    const view = this.view;
    const {organizationId, jobId} = this.jobs;
    if (
      !view ||
      !jobId ||
      this.checkingCredits ||
      this.busy ||
      !hasCreditHold(view)
    )
      return;
    const current = this.scope.begin();
    this.checkingCredits = true;
    this.notice = null;
    try {
      const {settled} = await studioRequest(organizationId, org =>
        this.repository.creditCheck(org, jobId),
      ).catch(e => {
        if (cannotCheckCredits(e)) return {settled: false};
        throw e;
      });
      if (!current()) return;
      await this.jobs.reload();
      // Settled meanwhile by the background pass, or by work that started: nothing to say.
      const stillLocked = hasCreditHold(this.view) && !this.view?.running;
      if (current() && !settled && stillLocked)
        runInAction(() => {
          this.notice = {
            kind: 'error',
            text: creditsNotSettled(this.view),
            creditCheck: true,
          };
        });
    } catch (e) {
      if (current()) this.fail(errorMessage(e));
    } finally {
      if (current())
        runInAction(() => {
          this.checkingCredits = false;
        });
    }
  }

  /** Stops what Studio is doing on the job, then reads it again. */
  async cancel() {
    if (this.canceling) return;
    const current = this.scope.begin();
    this.canceling = true;
    try {
      await this.jobs.cancel();
    } catch (e) {
      if (current()) this.fail(errorMessage(e));
    } finally {
      if (current())
        runInAction(() => {
          this.canceling = false;
        });
    }
  }

  chooseTopics(topics: string[]) {
    return this.send((organizationId, id, version) =>
      this.repository.chooseTopics(organizationId, id, topics, version),
    );
  }

  /** A microreel's changes; each resolves to the job the server returns. */
  reelActions(reelId: string): ReelActions {
    return {
      rename: title => this.renameReel(reelId, title),
      resolveSme: response =>
        this.send((organizationId, id, version) =>
          this.repository.resolveSme(
            organizationId,
            id,
            reelId,
            response,
            version,
          ),
        ),
      suggestQuestion: () =>
        this.send((organizationId, id, version) =>
          this.repository.suggestQuestion(organizationId, id, reelId, version),
        ),
      setEnding: body =>
        this.send((organizationId, id, version) =>
          this.repository.setEnding(organizationId, id, reelId, body, version),
        ),
      addScene: (afterSceneId, scene) =>
        this.send((organizationId, id, version) =>
          this.repository.addScene(
            organizationId,
            id,
            reelId,
            {afterSceneId, ...scene},
            version,
          ),
        ),
      tryAnotherLook: () =>
        this.send((organizationId, id, version) =>
          this.repository.tryAnotherLook(organizationId, id, reelId, version),
        ),
      updateMotion: change =>
        this.send((organizationId, id, version) =>
          this.repository.updateReelMotion(
            organizationId,
            id,
            reelId,
            change,
            version,
          ),
        ),
    };
  }

  /** One scene's changes; each resolves to the job the server returns. */
  sceneActions(reelId: string, sceneId: string): SceneActions {
    return {
      edit: edit =>
        this.send((organizationId, id, version) =>
          this.repository.editScene(
            organizationId,
            id,
            reelId,
            sceneId,
            edit,
            version,
          ),
        ),
      move: direction =>
        this.send((organizationId, id, version) =>
          this.repository.moveScene(
            organizationId,
            id,
            reelId,
            sceneId,
            direction,
            version,
          ),
        ),
      remove: () =>
        this.send((organizationId, id, version) =>
          this.repository.deleteScene(
            organizationId,
            id,
            reelId,
            sceneId,
            version,
          ),
        ),
      rewrite: instruction =>
        this.send((organizationId, id, version) =>
          this.repository.rewriteScene(
            organizationId,
            id,
            reelId,
            sceneId,
            instruction,
            version,
          ),
        ),
      approveOverride: () =>
        this.send((organizationId, id, version) =>
          this.repository.approveOverride(
            organizationId,
            id,
            reelId,
            sceneId,
            version,
          ),
        ),
      tryAnotherAnimation: () =>
        this.send((organizationId, id, version) =>
          this.repository.tryAnotherAnimation(
            organizationId,
            id,
            reelId,
            sceneId,
            version,
          ),
        ),
    };
  }

  /**
   * One request against the open job and the version shown, resolving to its data. The editor
   * that sent it decides whether the answer still belongs on the page.
   */
  private send<T>(
    request: (
      organizationId: string,
      id: string,
      version: StudioVersion,
    ) => Promise<StudioResponse<T>>,
  ): Promise<T> {
    const {organizationId, jobId} = this.jobs;
    const version = this.view?.job.storeVersion;
    const enforceAiCredits = this.view?.billing?.enforceAiCredits !== false;
    if (!jobId) return Promise.reject(new ApiError(GENERIC_ERROR, 0));
    return studioRequest(organizationId, org =>
      request(org, jobId, version),
    ).catch(error => {
      if (
        !enforceAiCredits &&
        (isInsufficientCredits(error) || isCreditCheckPending(error))
      )
        throw new ApiError(GENERIC_ERROR, error.status, error.fromServer);
      // The editor shows the message; `fail` then knows it is a refusal for credits.
      this.refused(error);
      throw error;
    });
  }

  /** A change made by the page itself: the job it returns is shown. */
  private async change(
    request: (
      organizationId: string,
      id: string,
      version: StudioVersion,
    ) => Promise<StudioResponse<JobView>>,
  ) {
    await this.jobs.mutate(({organizationId, id, storeVersion}) =>
      studioRequest(organizationId, org => request(org, id, storeVersion)),
    );
  }

  private reset() {
    this.notice = null;
    this.rendering = false;
    this.retrying = false;
    this.canceling = false;
    this.checkingCredits = false;
    this.creditsRefusal = null;
    this.creditCheckRefusal = null;
    this.openSettings = {};
    this.dirtyEditors = {};
    this.endingDrafts = {};
    this.pendingEndings = 0;
  }
}
